import { useEffect, useState } from "react";

import { toCount } from "common/utils/formatting";

import Modal from "client/common/components/deprecated-modals/Modal";
import ModalLayout from "client/common/components/deprecated-modals/ModalLayout";
import Accordion from "client/common/components/interactive/Accordion";

import { CATEGORY_PRODUCT_LINE_FILTER_DEFINITIONS } from "../constants/filterDefinitions";
import {
  FilterValue,
  ProductLineSearchFilters,
  SelectedCategoryProductLineFilters,
} from "../types";
import { clearedFilterValues } from "../utils";
import SearchFilter from "./SearchFilter";

type CategoryProductLineSearchFilterModalProps = {
  options: ProductLineSearchFilters;
  isVisible: boolean;
  onClose: () => void;
  value: SelectedCategoryProductLineFilters;
  onChange: (value: SelectedCategoryProductLineFilters) => void;
  showCounts: boolean;
  isCollapsible: boolean;
};

const CategoryProductLineSearchFilterModal = ({
  options,
  showCounts,
  isCollapsible,
  isVisible,
  onClose,
  value,
  onChange,
}: CategoryProductLineSearchFilterModalProps) => {
  const [updatedFilters, setUpdatedFilters] = useState(value);

  useEffect(() => {
    if (isVisible) {
      setUpdatedFilters(value);
    }
    // We want to ensure the params are up to date when the modal first comes into view
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const resetFilters = () => {
    setUpdatedFilters({
      ...updatedFilters,
      ...clearedFilterValues(CATEGORY_PRODUCT_LINE_FILTER_DEFINITIONS),
    });
  };

  const applyFilters = () => {
    onClose();
    onChange({ ...updatedFilters });
  };

  const accordionItems = CATEGORY_PRODUCT_LINE_FILTER_DEFINITIONS.map((definition) => ({
    key: definition.key,
    label: definition.label,
    params: definition.selectedFrom(updatedFilters),
    content: (
      <SearchFilter<FilterValue>
        showCounts={showCounts}
        isCollapsible={isCollapsible}
        options={definition.optionsFrom(options)}
        value={definition.selectedFrom(updatedFilters)}
        onChange={(next) => setUpdatedFilters({ ...updatedFilters, ...definition.toUpdate(next) })}
      />
    ),
  }));

  const filterCount = accordionItems.flatMap<number | string | boolean>(
    (item) => item.params,
  ).length;

  return (
    <Modal visible={isVisible} onClose={onClose}>
      <ModalLayout>
        <ModalLayout.Header title="Apply Filters" onClose={onClose} />
        <ModalLayout.Content>
          <Accordion.Multi
            items={accordionItems}
            initialOpenKeys={accordionItems.map((item) => item.key)}
          />
        </ModalLayout.Content>
        <ModalLayout.Actions>
          <ModalLayout.Actions.Button
            variant="secondary"
            onClick={resetFilters}
            disabled={!filterCount}
          >
            Reset {toCount(filterCount, "Filter")}
          </ModalLayout.Actions.Button>
          <ModalLayout.Actions.Button onClick={applyFilters}>Apply</ModalLayout.Actions.Button>
        </ModalLayout.Actions>
      </ModalLayout>
    </Modal>
  );
};

export default CategoryProductLineSearchFilterModal;
